/**
 * Stripe Provider Component
 * Provides basic Stripe context for payment processing
 * White-labeled for AmalQ Pay with Islamic finance features
 * Note: Elements component is now rendered at payment step level with clientSecret
 * 
 * @description This provider only manages the Stripe instance, not Elements.
 * Elements are created when we have a clientSecret in the payment step.
 */

import React, { createContext, useContext, useMemo, useEffect, useState } from 'react';
import { validateStripeConfig } from './config';
import { getStripeInstance } from './client';

export interface StripeProviderContext {
  /** Whether Stripe is loading */
  isLoading: boolean;
  /** Whether Stripe is ready */
  isReady: boolean;
  /** Any initialization errors */
  error: string | null;
  /** Stripe.js instance promise for use in Elements */
  stripePromise: Promise<import('@stripe/stripe-js').Stripe | null>;
}

const StripeContext = createContext<StripeProviderContext | null>(null);

/**
 * Hook to use Stripe provider context
 * @returns StripeProviderContext with Stripe instance and loading state
 */
export const useStripeProvider = (): StripeProviderContext => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripeProvider must be used within a StripeProvider');
  }
  return context;
};

export interface StripeProviderProps {
  /** Child components that need Stripe context */
  children: React.ReactNode;
}

/**
 * Stripe Provider Component
 * Provides basic Stripe.js instance to child components
 * Elements component is created separately when clientSecret is available
 */
export const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stripePromise = useMemo(() => {
    try {
      validateStripeConfig();
      return getStripeInstance();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Stripe';
      console.error('Stripe initialization failed:', errorMessage);
      return Promise.resolve(null);
    }
  }, []);

  // Initialize Stripe and update loading state
  useEffect(() => {
    const initializeStripe = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);
        
        const stripe = await stripePromise;
        
        if (stripe) {
          setIsReady(true);
        } else {
          throw new Error('Stripe failed to load');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Stripe initialization failed';
        setError(errorMessage);
        setIsReady(false);
        console.error('❌ Stripe initialization error:', errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    initializeStripe();
  }, [stripePromise]);

  const contextValue: StripeProviderContext = useMemo(() => ({
    isLoading,
    isReady,
    error,
    stripePromise,
  }), [isLoading, isReady, error, stripePromise]);


  return (
    <StripeContext.Provider value={contextValue}>
      {children}
    </StripeContext.Provider>
  );
};

export default StripeProvider;